'use client'

import { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { api } from '@/lib/api'
import { formatDT } from '@/lib/utils'
import { ArrowLeft, Search, Plus, Minus, Trash2, AlertTriangle, CheckCircle, Scan, X, Printer, User, ChevronDown } from 'lucide-react'
import Link from 'next/link'
import { BarcodeScanner, beep } from '@/components/BarcodeScanner'
import { useLang } from '@/context/LangContext'

interface CartItem {
  article: any
  quantite: number
  prix: number
}

interface VenteDone {
  total: number
  lignes: CartItem[]
  time: string
  date: string
  clientName?: string
  soldeApres?: number
}

// ─── Prix input inline ────────────────────────────────────────────────────────
function PrixInput({ item, onValidate }: { item: CartItem; onValidate: (id: string, prix: number) => void }) {
  const prix = parseFloat(item.prix as any) || 0
  const [val, setVal] = useState(prix.toFixed(3))
  useEffect(() => { setVal((parseFloat(item.prix as any) || 0).toFixed(3)) }, [item.prix])
  return (
    <div className="relative">
      <input
        type="number" inputMode="decimal"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onValidate(item.article.id, parseFloat(val) || 0)}
        className={`w-24 px-2 py-1.5 pr-6 rounded-xl border text-sm font-bold outline-none text-right ${
          item.prix < item.article.prix_min
            ? 'border-red-400 bg-red-50 text-red-600'
            : 'border-gray-200 bg-gray-50 focus:border-indigo-400'
        }`}
      />
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">DT</span>
    </div>
  )
}

// ─── Ticket ───────────────────────────────────────────────────────────────────
function TicketModal({ data, shopName, onClose }: { data: VenteDone; shopName: string; onClose: () => void }) {
  return (
    <>
      <style>{`
        @media print {
          body > * { display: none !important; }
          #ticket-print { display: block !important; }
        }
        #ticket-print { display: none; }
      `}</style>
      <div id="ticket-print" style={{ fontFamily: 'monospace', fontSize: 12, width: '80mm', margin: '0 auto', padding: 8 }}>
        <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: 6, marginBottom: 6 }}>
          <div style={{ fontWeight: 'bold', fontSize: 15 }}>{shopName}</div>
          <div>{data.date} — {data.time}</div>
        </div>
        {data.lignes.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ flex: 1 }}>{item.article.name.substring(0, 22)}</span>
            <span style={{ marginLeft: 8 }}>x{item.quantite}</span>
            <span style={{ marginLeft: 8, minWidth: 60, textAlign: 'right' }}>{(item.prix * item.quantite).toFixed(3)}</span>
          </div>
        ))}
        <div style={{ borderTop: '1px dashed #000', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 14 }}>
          <span>TOTAL</span><span>{data.total.toFixed(3)} DT</span>
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11 }}>— Merci pour votre achat! —</div>
      </div>

      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        <div className="bg-white shadow-sm flex-shrink-0">
          <div className="flex items-center gap-3 px-4 pt-12 pb-4">
            <button onClick={onClose} className="p-2 -ml-2 text-gray-600"><X className="w-6 h-6" /></button>
            <h2 className="text-xl font-bold text-gray-900 flex-1">Ticket de vente ✅</h2>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-5 font-mono text-sm max-w-sm mx-auto shadow-sm">
            <div className="text-center border-b border-dashed border-gray-300 pb-3 mb-3">
              <p className="font-bold text-base">{shopName}</p>
              <p className="text-gray-500 text-xs">{data.date} — {data.time}</p>
            </div>
            <div className="space-y-2 mb-3">
              {data.lignes.map((item, i) => (
                <div key={i} className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-gray-800 font-semibold">{item.article.name}</p>
                    <p className="text-xs text-gray-400">x{item.quantite} × {item.prix.toFixed(3)} DT</p>
                  </div>
                  <p className="font-bold text-gray-900 whitespace-nowrap">{(item.prix * item.quantite).toFixed(3)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-dashed border-gray-300 pt-3 flex justify-between items-center">
              <span className="font-bold text-gray-600">TOTAL</span>
              <span className="text-2xl font-bold text-gray-900">{data.total.toFixed(3)} DT</span>
            </div>
            {data.clientName && (
              <div className={`mt-3 rounded-xl p-3 text-center text-sm ${(data.soldeApres ?? 0) < 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <p className="font-semibold text-gray-700">👤 {data.clientName}</p>
                <p className={`font-bold ${(data.soldeApres ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {(data.soldeApres ?? 0) < 0
                    ? `⚠️ Reste à payer: ${formatDT(Math.abs(data.soldeApres ?? 0))}`
                    : `✅ Crédit restant: ${formatDT(data.soldeApres ?? 0)}`}
                </p>
              </div>
            )}
            <p className="text-center text-gray-400 text-xs mt-3">— Merci pour votre achat! —</p>
          </div>
        </div>
        <div className="px-4 pb-10 pt-3 space-y-3 bg-white border-t border-gray-100">
          <button onClick={() => window.print()}
            className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg active:scale-95 flex items-center justify-center gap-2">
            <Printer className="w-5 h-5" /> Imprimer le ticket
          </button>
          <button onClick={onClose} className="w-full py-4 bg-gray-100 text-gray-700 rounded-2xl font-bold text-lg active:scale-95">
            Fermer
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function VentePage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()
  const searchRef = useRef<HTMLInputElement>(null)

  const [articles, setArticles] = useState<any[]>([])
  const [filtered, setFiltered] = useState<any[]>([])
  const [search, setSearch] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [showScanner, setShowScanner] = useState(false)
  const [scanMsg, setScanMsg] = useState('')
  const [priceAlert, setPriceAlert] = useState<{ item: CartItem; newPrix: number } | null>(null)
  const [saving, setSaving] = useState(false)
  const [venteDone, setVenteDone] = useState<VenteDone | null>(null)
  // Client
  const [clients, setClients] = useState<any[]>([])
  const [selectedClient, setSelectedClient] = useState<any>(null)
  const [clientSearch, setClientSearch] = useState('')
  const [showClientPicker, setShowClientPicker] = useState(false)
  const [paiementNow, setPaiementNow] = useState('')

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    loadArticles()
    loadClients()
  }, [session])

  async function loadClients() {
    const data = await api.getClients(session!.shop.id)
    setClients(data || [])
  }

  useEffect(() => {
    if (!search.trim()) { setFiltered([]); return }
    const q = search.toLowerCase()
    setFiltered(articles.filter(a =>
      a.name.toLowerCase().includes(q) || a.barcode?.toLowerCase().includes(q)
    ).slice(0, 15))
  }, [search, articles])

  async function loadArticles() {
    const data = await api.getArticles(session!.shop.id, { inStock: true })
    setArticles(data || [])
  }

  function onBarcodeDetected(code: string) {
    setShowScanner(false)
    const article = articles.find(a => a.barcode === code)
      ?? articles.find(a => a.barcode?.toUpperCase() === code.toUpperCase())
    if (!article) {
      setScanMsg(`❌ "${code}" — article introuvable`)
      setTimeout(() => setScanMsg(''), 3000)
      return
    }
    addToCart(article)
  }

  function addToCart(article: any) {
    beep()
    setCart(prev => {
      const existing = prev.find(i => i.article.id === article.id)
      if (existing) {
        if (existing.quantite >= article.stock) return prev
        return prev.map(i => i.article.id === article.id ? { ...i, quantite: i.quantite + 1 } : i)
      }
      return [...prev, { article, quantite: 1, prix: parseFloat(article.prix_vente) || 0 }]
    })
    setSearch('')
  }

  function updateQty(id: string, delta: number) {
    setCart(prev =>
      prev
        .map(i => i.article.id === id ? { ...i, quantite: Math.max(0, Math.min(i.article.stock, i.quantite + delta)) } : i)
        .filter(i => i.quantite > 0)
    )
  }

  function updatePrix(id: string, newPrix: number) {
    const item = cart.find(i => i.article.id === id)
    if (!item) return
    if (newPrix < item.article.prix_min) {
      setPriceAlert({ item: { ...item, prix: newPrix }, newPrix })
      return
    }
    setCart(prev => prev.map(i => i.article.id === id ? { ...i, prix: newPrix } : i))
  }

  function confirmPrixAlert() {
    if (!priceAlert) return
    setCart(prev => prev.map(i => i.article.id === priceAlert.item.article.id ? { ...i, prix: priceAlert.newPrix } : i))
    setPriceAlert(null)
  }

  const total = cart.reduce((s, i) => s + (parseFloat(i.prix as any) || 0) * i.quantite, 0)
  const totalQty = cart.reduce((s, i) => s + i.quantite, 0)
  // Paiement à la vente plafonné au total (rembourser une vieille dette = bouton "Paiement reçu")
  const pNow = Math.min(parseFloat(paiementNow) || 0, total)
  const soldeCourantClient = parseFloat(selectedClient?.solde) || 0
  const detteAjouteeLive = Math.max(0, total - pNow)            // dette créée par CETTE vente
  const soldeApresVente = soldeCourantClient - detteAjouteeLive  // solde total du client après
  const clientDoit = selectedClient && soldeApresVente < -0.0001 // le client reste débiteur

  async function confirmVente() {
    if (!cart.length) return
    setSaving(true)
    const lignesSnap = [...cart]
    const totalSnap = total
    const clientSnap = selectedClient
    const paiementSnap = clientSnap ? Math.min(parseFloat(paiementNow) || 0, totalSnap) : totalSnap
    const soldeCourant = parseFloat(clientSnap?.solde ?? '0') || 0
    // Cash réellement encaissé pour CETTE vente
    const montantEncaisse = clientSnap ? paiementSnap : totalSnap
    // Dette NETTE ajoutée au compte client (jamais négative ici)
    const detteAjoutee = clientSnap ? (totalSnap - paiementSnap) : 0

    const vente = await api.createVente({
      shop_id: session!.shop.id,
      user_id: session!.user.id,
      total: totalSnap,
      montant_encaisse: montantEncaisse,
      lignes: lignesSnap.map(i => ({
        article_id: i.article.id,
        quantite: i.quantite,
        prix_unitaire: i.prix,
        ras_mel_unitaire: i.article.ras_mel,
      })),
    })

    let soldeApres: number | undefined
    if (clientSnap) {
      soldeApres = soldeCourant - detteAjoutee
      // Enregistre uniquement la dette nette (si > 0). Le cash payé est déjà dans montant_encaisse.
      if (detteAjoutee > 0.0001) {
        await fetch(`/api/clients/${clientSnap.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'vente',
            montant: detteAjoutee,
            note: `Achat ${formatDT(totalSnap)}${paiementSnap > 0 ? ` (payé ${formatDT(paiementSnap)})` : ''}`,
            vente_id: vente?.id || null,
          }),
        })
      }
      setSelectedClient(null)
      setPaiementNow('')
    }

    setCart([])
    setSaving(false)
    setVenteDone({
      total: totalSnap,
      lignes: lignesSnap,
      time: new Date().toLocaleTimeString('fr-TN', { hour: '2-digit', minute: '2-digit' }),
      date: new Date().toLocaleDateString('fr-TN'),
      clientName: clientSnap?.name,
      soldeApres,
    })
    loadClients()
    loadArticles()
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">

      {showScanner && (
        <BarcodeScanner title="Scanner article" onDetected={onBarcodeDetected} onClose={() => setShowScanner(false)} />
      )}
      {venteDone && (
        <TicketModal data={venteDone} shopName={session?.shop?.name || 'Boutique'} onClose={() => setVenteDone(null)} />
      )}

      {/* ── Header ── */}
      <div className="bg-white shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">{t('vente_title')}</h1>
          {cart.length > 0 && (
            <button onClick={() => setCart([])} className="text-red-400 text-sm font-semibold px-3 py-1.5 rounded-xl bg-red-50 active:scale-95">
              {t('empty_cart')}
            </button>
          )}
        </div>
        {/* Search + Scan */}
        <div className="px-4 pb-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('search_article')}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:bg-white"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowScanner(true)}
            className="w-12 h-12 bg-red-500 rounded-2xl flex items-center justify-center text-white active:scale-95 shadow-sm"
          >
            <Scan className="w-5 h-5" />
          </button>
        </div>
        {scanMsg && (
          <div className="mx-4 mb-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm font-medium">
            {scanMsg}
          </div>
        )}
      </div>

      {/* ── Contenu ── */}
      <div className="flex-1 overflow-y-auto">

        {/* ── Panier — toujours visible ── */}
        {cart.length > 0 && (
          <div className="px-4 pt-3 space-y-2">
            {/* Titre panier */}
            <div className="flex items-center justify-between px-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                Panier — {totalQty} article{totalQty > 1 ? 's' : ''}
              </p>
            </div>

            {cart.map(item => (
              <div key={item.article.id} className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
                {/* Ligne 1: nom + suppr */}
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-gray-800 flex-1 truncate pr-2 text-sm">{item.article.name}</p>
                  <button onClick={() => setCart(c => c.filter(i => i.article.id !== item.article.id))}>
                    <Trash2 className="w-4 h-4 text-red-300" />
                  </button>
                </div>
                {/* Ligne 2: quantité + prix + total */}
                <div className="flex items-center gap-2">
                  {/* +/- */}
                  <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                    <button
                      onClick={() => updateQty(item.article.id, -1)}
                      className="w-7 h-7 bg-white rounded-lg shadow-sm flex items-center justify-center active:scale-90"
                    >
                      <Minus className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <span className="w-7 text-center font-bold text-gray-800 text-sm">{item.quantite}</span>
                    <button
                      onClick={() => updateQty(item.article.id, +1)}
                      className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center active:scale-90"
                    >
                      <Plus className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                  {/* Prix unitaire */}
                  <PrixInput item={item} onValidate={updatePrix} />
                  {/* Total ligne */}
                  <p className="ml-auto font-bold text-gray-900 text-sm whitespace-nowrap">
                    = {formatDT(item.prix * item.quantite)}
                  </p>
                </div>
                {item.prix < item.article.prix_min && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Sous le minimum ({formatDT(item.article.prix_min)})
                  </p>
                )}
              </div>
            ))}

            {/* Séparateur si recherche active */}
            {filtered.length > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">Résultats</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            )}
          </div>
        )}

        {/* ── Résultats de recherche ── */}
        {filtered.length > 0 && (
          <div className="px-4 pt-2 pb-2 space-y-2">
            {filtered.map(article => {
              const inCart = cart.find(i => i.article.id === article.id)
              return (
                <button
                  key={article.id}
                  onClick={() => addToCart(article)}
                  className={`w-full bg-white rounded-2xl px-4 py-3.5 flex items-center justify-between shadow-sm border-2 active:scale-95 transition-all ${inCart ? 'border-indigo-300 bg-indigo-50' : 'border-gray-100'}`}
                >
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">{article.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Stock: {article.stock} {article.unit}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="font-bold text-gray-900">{formatDT(article.prix_vente)}</p>
                    {inCart && <p className="text-xs text-indigo-500 font-semibold">× {inCart.quantite} dans panier</p>}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {/* ── État vide ── */}
        {cart.length === 0 && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 opacity-30">
            <Scan className="w-16 h-16 text-gray-400" />
            <p className="text-gray-500 font-medium text-center whitespace-pre-line">{t('cart_empty')}</p>
          </div>
        )}
      </div>

      {/* ── Barre total + Confirmer ── */}
      {cart.length > 0 && (
        <div className="bg-white border-t border-gray-100 px-4 py-4 flex-shrink-0 space-y-3">
          {/* Sélecteur client */}
          <div
            onClick={() => !selectedClient && setShowClientPicker(true)}
            className={`w-full px-4 py-3 rounded-2xl border-2 flex items-center gap-3 transition-all ${selectedClient ? 'border-indigo-300 bg-indigo-50' : 'border-dashed border-gray-200 bg-gray-50 active:scale-95 cursor-pointer'}`}>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedClient ? 'bg-indigo-600' : 'bg-gray-200'}`}>
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 text-left" onClick={() => selectedClient && setShowClientPicker(true)}>
              {selectedClient ? (
                <p className="font-semibold text-indigo-700 text-sm">{selectedClient.name}</p>
              ) : (
                <p className="text-gray-400 text-sm">Associer un client (optionnel)</p>
              )}
            </div>
            {selectedClient ? (
              <button onClick={e => { e.stopPropagation(); setSelectedClient(null); setPaiementNow('') }} className="text-gray-400 p-1 active:scale-90">
                <X className="w-4 h-4" />
              </button>
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>

          {/* Total */}
          <div className="flex items-center justify-between px-1">
            <span className="text-gray-500 font-medium">{totalQty} article{totalQty > 1 ? 's' : ''}</span>
            <span className="text-2xl font-bold text-gray-900">{formatDT(total)}</span>
          </div>

          {/* Paiement immédiat — seulement si client sélectionné */}
          {selectedClient && (
            <div className={`rounded-2xl border-2 p-3 space-y-2 ${clientDoit ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                {detteAjouteeLive > 0.0001 ? '💳 Kardé — paiement partiel' : '✅ Payé en entier'}
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Payé maintenant :</label>
                <div className="relative flex-1">
                  <input
                    type="number" inputMode="decimal"
                    value={paiementNow}
                    onChange={e => setPaiementNow(e.target.value)}
                    placeholder="0.000"
                    className="w-full px-3 py-2 pr-8 rounded-xl border border-gray-200 bg-white outline-none focus:border-indigo-400 text-sm font-bold text-right"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">DT</span>
                </div>
                <button
                  onClick={() => setPaiementNow(total.toFixed(3))}
                  className="text-xs px-2.5 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg font-semibold whitespace-nowrap active:scale-95"
                >
                  Tout
                </button>
              </div>
              {/* Kardé de cette vente */}
              {detteAjouteeLive > 0.0001 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Kardé cette vente</span>
                  <span className="font-bold text-orange-600">{formatDT(detteAjouteeLive)}</span>
                </div>
              )}
              {/* Solde total du client après cette vente */}
              <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                <span className="text-gray-500">Solde client après</span>
                <span className={`font-bold ${clientDoit ? 'text-red-600' : 'text-green-600'}`}>
                  {clientDoit
                    ? `doit ${formatDT(Math.abs(soldeApresVente))}`
                    : soldeApresVente > 0.0001
                      ? `crédit ${formatDT(soldeApresVente)}`
                      : 'soldé ✓'}
                </span>
              </div>
            </div>
          )}

          <button onClick={confirmVente} disabled={saving}
            className="w-full py-4 bg-green-500 text-white rounded-2xl font-bold text-xl active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving
              ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <><CheckCircle className="w-6 h-6" /> Confirmer — {formatDT(total)}</>}
          </button>
        </div>
      )}

      {/* ── Client picker ── */}
      {showClientPicker && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowClientPicker(false)} />
          <div className="relative w-full bg-white rounded-t-3xl p-5 shadow-2xl max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Choisir un client</h2>
              <button onClick={() => setShowClientPicker(false)} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                placeholder="Chercher..." autoFocus
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 rounded-xl border border-gray-200 outline-none focus:border-indigo-400 text-sm" />
            </div>
            <div className="overflow-y-auto space-y-2">
              {clients
                .filter(c => !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase()) || c.phone?.includes(clientSearch))
                .map(c => {
                  const solde = parseFloat(c.solde) || 0
                  return (
                    <button key={c.id} onClick={() => { setSelectedClient(c); setShowClientPicker(false); setClientSearch('') }}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-2xl active:bg-indigo-50 text-left">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold flex-shrink-0 ${solde < 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 text-sm">{c.name}</p>
                        {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                      </div>
                      <p className={`text-sm font-bold ${solde < 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {solde > 0 ? '+' : ''}{formatDT(solde)}
                      </p>
                    </button>
                  )
                })}
            </div>
          </div>
        </div>
      )}

      {/* ── Alerte prix min ── */}
      {priceAlert && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPriceAlert(null)} />
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-center text-gray-800 mb-1">⚠️ Prix trop bas!</h3>
            <p className="text-center text-gray-500 text-sm mb-4">
              Prix min: <strong>{formatDT(priceAlert.item.article.prix_min)}</strong><br />
              Tu veux vendre à: <strong className="text-red-500">{formatDT(priceAlert.newPrix)}</strong>
            </p>
            <div className="flex gap-3">
              <button onClick={() => setPriceAlert(null)} className="flex-1 py-3 bg-gray-100 rounded-2xl font-semibold text-gray-700">Annuler</button>
              <button onClick={confirmPrixAlert} className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-semibold">Confirmer quand même</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
